import { Controller } from "@hotwired/stimulus"
import TableFrameNavigator from "../../utils/table_frame_navigator"
import TableQueryState from "../../utils/table_query_state"

// Connects to data-controller="realities--filter"
// Handles filter logic and URL construction for realities
export default class extends Controller {
  static targets = ["status", "propertyType", "search", "order"]
  static outlets = ["realities--filter-menu"]

  static singleValueTargets = ["status", "propertyType", "search", "order"]

  connect() {
    this.tableNavigator = new TableFrameNavigator({
      frameId: "realities_list",
      tableKey: "realities",
      onPopState: (queryState) => this.setFilterValues(queryState)
    })
    this.setFilterValues()
  }

  disconnect() {
    this.tableNavigator?.disconnect()
  }

  setFilterValues(queryState = new TableQueryState(window.location.search)) {
    const urlSearchParams = queryState.toSearchParams()
    this.constructor.singleValueTargets.forEach((targetName) => {
      const hasTargetProperty = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTargetProperty]) {
        const target = this[`${targetName}Target`]
        if (target) {
          target.value = urlSearchParams.has(targetName) ? urlSearchParams.get(targetName) : ""
        }
      }
    })
  }

  activeStatusChecker(event) {
    if (!this.hasRealitiesFilterMenuOutlet) {
      return
    }
    const filterMenuController = this.realitiesFilterMenuOutlet
    const mapping = {
      status: [["statusTarget"], filterMenuController.activeStatusStatusTarget],
      propertyType: [["propertyTypeTarget"], filterMenuController.activeStatusPropertyTypeTarget],
      order: [["orderTarget"], filterMenuController.activeStatusOrderTarget]
    }

    for (const key in mapping) {
      if (event.target.name.includes(key)) {
        const [targets, statusTarget] = mapping[key]
        if (statusTarget) {
          this.#statusChecker(filterMenuController, targets, statusTarget)
        }
        break
      }
    }
  }

  applySearch() {
    this.#constructUrlAndSendRequest({ search: true })
  }

  cancelFilters() {
    this.removeValuesFromFilters()
    this.#constructUrlAndSendRequest()
  }

  removeValuesFromFilters() {
    this.constructor.singleValueTargets.forEach((targetName) => {
      const hasTargetProperty = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTargetProperty]) {
        const target = this[`${targetName}Target`]
        target.value = ""
        target.disabled = false
      }
    })
  }

  cancelChipFilter(event) {
    const targetName = event.currentTarget.dataset.target
    const target = this[`${targetName}Target`]
    if (target) {
      target.value = ""
      target.disabled = false
      this.#constructUrlAndSendRequest()
    }
  }

  applyFilters(event) {
    this.#constructUrlAndSendRequest({ search: event?.target?.name === "search" })
  }

  #constructUrlAndSendRequest({ search = false } = {}) {
    const queryState = this.#constructQueryState()
    if (search) {
      // Deliberately NO restoreFocusSelector, unlike contacts: this list's search
      // field is `data-turbo-permanent`, so Turbo MOVES the same node into the
      // replacement frame (FrameRenderer#render → preservingPermanentElements)
      // and its Bardo re-focuses whatever held focus inside it. Naming a restore
      // target on top of that re-runs table_frame_navigator's
      // setSelectionRange(end, end) up to three times per render, which yanks the
      // caret to the end of the field mid-word. Contacts needs the option because
      // its field is NOT permanent and is genuinely rebuilt.
      this.tableNavigator.scheduleSearch(queryState, { preserveScroll: { x: true, y: false } })
    } else {
      this.tableNavigator.navigate(queryState, {
        history: "push",
        reason: "filter",
        preserveScroll: { x: true, y: false }
      })
    }
  }

  #statusChecker(filterMenuController, targets, statusTarget) {
    const status = targets.some((target) => {
      const targetName = target.replace("Target", "")
      const hasTargetProperty = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (!this[hasTargetProperty]) {
        return false
      }
      const element = this[target]
      if (element.type === "checkbox") {
        return element?.checked
      } else {
        return element.value
      }
    })
    filterMenuController.toggleActiveStatus(status, statusTarget)
  }

  #constructQueryState() {
    const changes = Object.fromEntries(this.constructor.singleValueTargets.map((key) => [key, null]))
    this.constructor.singleValueTargets.forEach((targetName) => {
      const hasTargetProperty = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTargetProperty]) {
        const target = this[`${targetName}Target`]
        if (target?.value && target?.value !== "") {
          changes[targetName] = target.value
        }
      }
    })
    return new TableQueryState(window.location.search).patch(changes)
  }
}
